<script setup lang="ts">
import getPressReleasesByYearQuery from '@/graphql/getPressReleasesByYear.gql'
import Pagination from '~/components/utilities/Pagination.vue'

const localePath = useLocalePath()
const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()

defineI18nRoute({
  paths: {
    'en-CA': '/news-events/press-releases/:year',
    'fr-CA': '/nouvelles-evenements/communiques-presse/:year'
  }
})

useHead({
  title: t('navigation.pressReleases.metatitle'),
  meta: [{ name: 'description', content: t('navigation.pressReleases.metadescription') }]
})

const { execute: getPressReleasesByYear } = useApolloFetch(getPressReleasesByYearQuery, 'pressReleases')
const year = parseInt(route.params.year as string, 10)
const { startISOString, endISOString } = getYearStartAndEndDates(year)

export type PressRelease = {
  id: number
  Name: string
  PublishedAtDate: string
  Document: {
    url: string
    size: number
  }
}

const pageSize = ref<number>(parseQueryParamToNumber(route.query.pageSize, 10))
const page = ref<number>(1)
const sortBy = ref<string>(getSortByParamOrDefault('PublishedAtDate:desc'))
const pressReleasesResponse = ref<{ data: PressRelease[], meta: any }>(await getPressReleases())

const pressReleases = computed(() => pressReleasesResponse.value.data)
const totalPressReleases = computed(() => pressReleasesResponse.value.meta.pagination.total)
const resultsIndexes = computed(() => {
  return `${(page.value - 1) * pageSize.value + 1} - ${Math.min(page.value * pageSize.value, totalPressReleases.value)}`
})

function getFormattedPublishDate(pressRelease: any) {
  const date = new Date(pressRelease.PublishedAtDate)
  return date.toLocaleDateString(locale.value, { year: 'numeric', month: 'long', day: 'numeric' })
}

async function getPressReleases() {
  return await getPressReleasesByYear({
    locale: locale.value,
    pageSize: pageSize.value,
    page: page.value,
    sortBy: sortBy.value,
    startDate: startISOString,
    endDate: endISOString
  })
}

function getSortByParamOrDefault(defaultValue: string): string {
  const param = route.query.sortBy
  if (Array.isArray(param)) {
    return param[0] ?? defaultValue
  }

  return param ?? defaultValue
}

function getSizeInMB(size: number) {
  return (size / 1024).toFixed(2)
}

function getYearStartAndEndDates(year: number) {
  const startDate = new Date(year, 0, 1, 0, 0, 0, 0)
  const endDate = new Date(year, 11, 31, 23, 59, 59, 999)

  const startISOString = startDate.toISOString()
  const endISOString = endDate.toISOString()

  return {
    startISOString,
    endISOString
  }
}

function parseQueryParamToNumber(param: string | (string | null)[] | null | undefined, defaultValue: number): number {
  if (Array.isArray(param)) {
    param = param[0]
  }
  const parsed = parseInt(param || '', 10)
  return isNaN(parsed) ? defaultValue : parsed
};

async function refreshPressReleases() {
  pressReleasesResponse.value = await getPressReleases()
}

watch([pageSize, sortBy, page], async ([_newPageSize, _newSortBy, newPage], [_oldPageSize, _oldSortBy, oldPage]) => {
  if (newPage === oldPage) {
    page.value = 1
  }

  router.push({
    path: route.path,
    query: {
      ...route.query,
      pageSize: pageSize.value.toString(),
      sortBy: sortBy.value,
      page: page.value.toString()
    }
  })

  await refreshPressReleases()
})
</script>

<template>
  <div class="wrapContent">
    <div class="entetePage">
      <div class="wrapper">
        <div class="filAriane">
          <span class="">
            <NuxtLink :to="localePath('index')">{{ $t('navigation.home.name') }}</NuxtLink>
            <span class=""> &gt; </span>
            <span class="">{{ $t('navigation.newsEvents.name') }}</span>
            <span class=""> &gt; </span>
            <span class="">{{ $t('navigation.pressReleases.name') }}</span>
          </span>
        </div>
        <div class="title">
          <h1><span class="">{{ $t('navigation.pressReleases.name') }}</span></h1>
        </div>
      </div>
    </div>
    <div class="contentPage">
      <div class="wrapper clearfix">
        <MenuNewsEvents />
        <div class="contenu right FacetedExplorer">
          <div class="GpcFacetedResults defaultFacet">
            <div class="enteteGPC">
              <div class="filterBlock">
                <label class="sortBy left">{{ $t("global.facets.sortBy") }}</label>
                <select v-model="sortBy">
                  <option value="PublishedAtDate:desc">
                    {{ $t("global.facets.dateDesc") }}
                  </option>
                  <option value="PublishedAtDate:asc">
                    {{ $t("global.facets.dateAsc") }}
                  </option>
                  <option value="Name:desc">
                    {{ $t("global.facets.nameDesc") }}
                  </option>
                  <option value="Name:asc">
                    {{ $t("global.facets.nameAsc") }}
                  </option>
                </select>
              </div>
              <div class="filterBlock">
                <label class="pageSize left">{{ $t("global.facets.itemsPerPage") }}</label>
                <select v-model="pageSize">
                  <option :value="10">
                    10
                  </option>
                  <option :value="25">
                    25
                  </option>
                  <option :value="50">
                    50
                  </option>
                  <option :value="100">
                    100
                  </option>
                </select>
              </div>
            </div>
            <div class="GpcResultItemWrapper">
              <div v-for="pressRelease in pressReleases"
                   :key="pressRelease.id"
                   class="FacetedResultTemplate DefaultResultContainer">
                <div class="ResultContent">
                  <div class="date">
                    {{ getFormattedPublishDate(pressRelease) }}
                  </div>
                  <div class="ResultContentProductName">
                    {{ pressRelease.Name }}
                  </div>
                  <a class="telechargement"
                     target="_blank"
                     :href="pressRelease.Document.url"><span>{{ $t("pressReleases.downloadPdf") }}&nbsp;-&nbsp;</span><span class="ProductBrokerType_Document">{{ getSizeInMB(pressRelease.Document.size) }} Mb</span></a>
                </div>
              </div>
            </div>
          </div>
          <div class="resultsPagination">
            <div class="results">
              <span>{{ $t("global.facets.results") }} <strong>{{ resultsIndexes }}</strong> {{ $t("global.facets.of") }} {{ totalPressReleases }}</span>
            </div>
            <div class="pagination">
              <Pagination v-model="page"
                          :total-items="totalPressReleases"
                          :page-size="pageSize" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss"></style>
